/**
 * Dashboard - Admin Overview Page
 * Simplified landing page with key metrics and navigation to detailed sections
 */

import React, { useState } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Box,
  BlockStack,
  InlineStack,
  Badge,
  Button,
  Grid,
  Spinner,
  Banner,
  Icon,
  Collapsible,
  Divider,
  InlineGrid,
} from '@shopify/polaris';
import {
  ArrowUpIcon,
  CashDollarIcon,
  OrderIcon,
  StoreIcon,
  ChartVerticalIcon,
  SettingsIcon,
  CheckCircleIcon,
  AlertTriangleIcon,
  DiscountIcon,
  CartSaleIcon,
  CashDollarFilledIcon,
} from '@shopify/polaris-icons';
import { useNavigate } from 'react-router-dom';

// Import analytics components
import MetricCard, { RevenueCard, CommissionCard } from '../components/Analytics/MetricCard';
import { useBrands, useDetailedAnalytics } from '../utils/queries';
import { validateAnalyticsData, validateBrandsData, createSafeDataAccessor, validateApiParams } from '../utils/dataValidation';

export default function Dashboard() {
  const navigate = useNavigate();
  const [syncErrorCollapsibleOpen, setSyncErrorCollapsibleOpen] = useState(false);

  // Fetch data for overview with validated parameters
  const apiParams = React.useMemo(() => validateApiParams({ brand: 'all', days: 30 }), []);

  const { data: brandsResponse, isLoading: brandsLoading, error: brandsError } = useBrands();
  const {
    data: analyticsResponse,
    isLoading: analyticsLoading,
    error: analyticsError
  } = useDetailedAnalytics(apiParams);

  // Validate and sanitize data with safe accessors
  const brandsAccessor = React.useMemo(() =>
    createSafeDataAccessor(brandsResponse, validateBrandsData),
    [brandsResponse]
  );

  const analyticsAccessor = React.useMemo(() =>
    createSafeDataAccessor(analyticsResponse, validateAnalyticsData),
    [analyticsResponse]
  );

  const brands = brandsAccessor.data;
  const analytics = analyticsAccessor.data;

  // Calculate system health metrics with null safety
  const systemHealth = React.useMemo(() => {
    if (!Array.isArray(brands) || !brands.length) return null;

    // Safe filtering with null checks
    const activeBrands = brands.filter(b => b && typeof b === 'object' && b.isActive === true).length;
    const totalBrands = brands.length;
    const syncErrors = brands.filter(b => b && typeof b === 'object' && b.lastSyncError);

    // Prevent division by zero
    const healthScore = totalBrands === 0 ? 0 :
      syncErrors === 0 ? 100 :
        Math.max(0, Math.min(100, 100 - (syncErrors / totalBrands) * 100));

    return {
      activeBrands,
      totalBrands,
      syncErrors,
      healthScore: Math.round(healthScore) // Ensure integer result
    };
  }, [brands]);

  // Quick action buttons
  const quickActions = [
    {
      title: 'View Analytics',
      description: 'Detailed cross-brand performance analysis',
      icon: ChartVerticalIcon,
      action: () => navigate('/analytics'),
      primary: true,
    },
    {
      title: 'Manage Orders',
      description: 'View and manage orders across all brands',
      icon: OrderIcon,
      action: () => navigate('/orders'),
    },
    {
      title: 'Brand Management',
      description: 'View and configure all brands',
      icon: SettingsIcon,
      action: () => navigate('/brands'),
    },
  ];

  // System status indicators
  const getSystemStatus = () => {
    if (!systemHealth) return { status: 'loading', message: 'Loading system status...' };
    const errorBrands = systemHealth.syncErrors.reduce((acc, item) => {
      acc.push({ id: item.id, name: item.name })
      return acc
    }, [])
    if (systemHealth.syncErrors.length > 0) {
      return {
        status: 'warning',
        message: `${systemHealth.syncErrors.length} brand(s) have sync issues`,
        brands: systemHealth.syncErrors.reduce((acc, item) => {
          acc.push({ id: item.id, name: item.name })
          return acc
        }, [])
      };
    }

    if (systemHealth.activeBrands === 0) {
      return {
        status: 'critical',
        message: 'No active brands'
      };
    }

    return {
      status: 'success',
      message: 'All systems operational'
    };
  };

  const systemStatus = getSystemStatus();

  if (brandsLoading || analyticsLoading) {
    return (
      <Page title="Dashboard" fullWidth>
        <Layout>
          <Layout.Section>
            <Card>
              <Box padding="800">
                <InlineStack align="center" gap="400">
                  <Spinner size="large" />
                  <Text variant="bodyLg">Loading dashboard...</Text>
                </InlineStack>
              </Box>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // Enhanced error handling with data validation feedback
  const hasErrors = brandsError || analyticsError || !brandsAccessor.isValid || !analyticsAccessor.isValid;

  if (hasErrors) {
    return (
      <Page title="Dashboard" fullWidth>
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <BlockStack gap="200">
                <Text fontWeight="semibold">Unable to load dashboard data</Text>
                {brandsError && (
                  <Text variant="bodySm">
                    Brands data: {process.env.NODE_ENV === 'development' ?
                      brandsError.message :
                      'Failed to load brand information'
                    }
                  </Text>
                )}
                {analyticsError && (
                  <Text variant="bodySm">
                    Analytics data: {process.env.NODE_ENV === 'development' ?
                      analyticsError.message :
                      'Failed to load analytics information'
                    }
                  </Text>
                )}
                {!brandsAccessor.isValid && <Text variant="bodySm">Invalid brands data format received</Text>}
                {!analyticsAccessor.isValid && <Text variant="bodySm">Invalid analytics data format received</Text>}
                <Text variant="bodySm">Please refresh the page or contact support if the issue persists.</Text>
              </BlockStack>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title="Dashboard"
      subtitle="System overview and performance metrics"
      fullWidth
    >
      <Layout>
        {/* System Status Banner */}
        {systemStatus.status === 'warning' && (
          <Layout.Section>
            <Banner tone="warning">
              <BlockStack gap={200}>
                <InlineStack gap="200" align="space-between" blockAlign="center">
                  <Text>{systemStatus.message}</Text>
                  <Button

                    size="micro"
                    onClick={() => setSyncErrorCollapsibleOpen(!syncErrorCollapsibleOpen)}
                  >
                    View Brands
                  </Button>
                </InlineStack>
                {
                  syncErrorCollapsibleOpen ?
                    <Collapsible open={syncErrorCollapsibleOpen}>
                      <InlineGrid columns={{ sm: 2, md: 2, lg: 3, xl: 5 }} gap={200}>
                        {systemStatus?.brands?.map((brand) => {
                          return <Button key={`${brand.id}-sync-errors`} onClick={() => navigate(`/brands/${brand.id}`)}>{brand.name}</Button>
                        })}
                      </InlineGrid>
                    </Collapsible> : null
                }
              </BlockStack>
            </Banner>
          </Layout.Section>
        )}

        {systemStatus.status === 'critical' && (
          <Layout.Section>
            <Banner tone="critical">
              <Text>{systemStatus.message}</Text>
            </Banner>
          </Layout.Section>
        )}

        {/* Data Quality Warning */}
        {(analyticsAccessor.hasData && !analyticsAccessor.isValid) ||
          (brandsAccessor.hasData && !brandsAccessor.isValid) ? (
          <Layout.Section>
            <Banner tone="warning">
              <InlineStack gap="200" align="space-between" blockAlign="center">
                <Text>Some dashboard data may be incomplete or incorrectly formatted. Please verify results.</Text>
                <Button
                  size="micro"
                  onClick={() => {
                    if (window.confirm('This will refresh the page and may lose any unsaved changes. Continue?')) {
                      window.location.reload();
                    }
                  }}
                >
                  Refresh Data
                </Button>
              </InlineStack>
            </Banner>
          </Layout.Section>
        ) : null}

        {/* Key Metrics Overview */}
        <Layout.Section>
          <Text variant="headingLg" as="h2">
            Performance Overview
          </Text>
          <Text variant="bodySm" tone="subdued">
            Last 30 days across all brands
          </Text>
        </Layout.Section>

        <Layout.Section>
          <Grid gap="400">
            <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 3, lg: 4, xl: 4 }}>
              <RevenueCard
                title="Gross Revenue"
                value={analytics.totalGrossRevenue}
                loading={analyticsLoading}
                error={analyticsError || (!analyticsAccessor.isValid ? 'Invalid data format' : null)}
                tooltip="Total revenue generated across all brands in the selected time period. Calculated from fulfilled orders."
                badge={!analyticsAccessor.hasData ? 'No Data' : null}
                icon={CashDollarIcon}
              />
            </Grid.Cell>

            <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 3, lg: 4, xl: 4 }}>
              <RevenueCard
                title="Discounts"
                value={analytics.totalDiscounts}
                loading={analyticsLoading}
                error={analyticsError || (!analyticsAccessor.isValid ? 'Invalid data format' : null)}
                tooltip="Total discounts."
                badge={!analyticsAccessor.hasData ? 'No Data' : null}
                icon={DiscountIcon}
              />
            </Grid.Cell>

            <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 3, lg: 4, xl: 4 }}>
              <RevenueCard
                title="Refunds"
                value={analytics.totalRefunds}
                loading={analyticsLoading}
                error={analyticsError || (!analyticsAccessor.isValid ? 'Invalid data format' : null)}
                tooltip="Total refunds."
                badge={!analyticsAccessor.hasData ? 'No Data' : null}
                icon={DiscountIcon}
              />
            </Grid.Cell>

            <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 3, lg: 4, xl: 4 }}>
              <RevenueCard
                title="Net Sales"
                value={analytics.totalNetSales}
                loading={analyticsLoading}
                error={analyticsError || (!analyticsAccessor.isValid ? 'Invalid data format' : null)}
                tooltip="Total sales after excluding discounts and refunds."
                badge={!analyticsAccessor.hasData ? 'No Data' : null}
                icon={CartSaleIcon}
              />
            </Grid.Cell>

            <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 3, lg: 4, xl: 4 }}>
              <CommissionCard
                title="Commission + Adjustments"
                value={analytics.totalCommission}
                loading={analyticsLoading}
                error={analyticsError || (!analyticsAccessor.isValid ? 'Invalid data format' : null)}
                tooltip="Total commission earned from fulfilled orders and adjustments. Based on commission rates and adjustments configured per brand and marketplace-specific overrides."
                badge={!analyticsAccessor.hasData ? 'No Data' : null}
                icon={CashDollarFilledIcon}
              />
            </Grid.Cell>

            <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 3, lg: 4, xl: 4 }}>
              <RevenueCard
                title="Shipping Fees"
                value={analytics.totalShippingFees}
                loading={analyticsLoading}
                error={analyticsError || (!analyticsAccessor.isValid ? 'Invalid data format' : null)}
                tooltip="Total shipping fees calculated based on admin configured rates based on weight and BoxC services."
                badge={!analyticsAccessor.hasData ? 'No Data' : null}
                icon={DiscountIcon}
              />
            </Grid.Cell>

            <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 3, lg: 4, xl: 4 }}>
              <RevenueCard
                title="Return Fees"
                value={analytics.totalReturnFees}
                loading={analyticsLoading}
                error={analyticsError || (!analyticsAccessor.isValid ? 'Invalid data format' : null)}
                tooltip="Total return fees calculated based on admin configured rates based on default rates and marketplace overrides."
                badge={!analyticsAccessor.hasData ? 'No Data' : null}
                icon={DiscountIcon}
              />
            </Grid.Cell>

            <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 3, lg: 4, xl: 4 }}>
              <RevenueCard
                title="Net Payout"
                value={analytics.totalPayout}
                loading={analyticsLoading}
                error={analyticsError || (!analyticsAccessor.isValid ? 'Invalid data format' : null)}
                tooltip="Total payout merchant will get after all discounts, refunds, commission, returns and shipping fees."
                badge={!analyticsAccessor.hasData ? 'No Data' : null}
                icon={DiscountIcon}
              />
            </Grid.Cell>

            <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 3, lg: 4, xl: 4 }}>
              <MetricCard
                title="Active Brands"
                value={systemHealth?.activeBrands || 0}
                loading={brandsLoading}
                error={brandsError || (!brandsAccessor.isValid ? 'Invalid data format' : null)}
                tooltip="Number of brands currently active and processing orders. Inactive brands are excluded from calculations."
                color="default"
                // subtitle={`${systemHealth?.totalBrands || 0} total brands`}
                badge={!brandsAccessor.hasData ? 'No Data' : null}
                icon={StoreIcon}
              />
            </Grid.Cell>
          </Grid>
        </Layout.Section>

        {/* Quick Actions */}
        <Layout.Section>
          <BlockStack gap="400">
            <Text variant="headingLg" as="h2">
              Quick Actions
            </Text>

            <Grid gap="400">
              {quickActions.map((action, index) => (
                <Grid.Cell
                  key={index}
                  columnSpan={{ xs: 6, sm: 6, md: 3, lg: 4, xl: 4 }}
                >
                  <Card>
                    <Box padding="100">
                      <BlockStack gap="400">
                        <InlineStack align="space-between" blockAlign="center">
                          <Text variant="headingSm" fontWeight="medium">
                            {action.title}
                          </Text>
                          <Box>
                            <Icon source={action.icon} tone="subdued" />
                          </Box>
                        </InlineStack>

                        <Text variant="bodySm" tone="subdued">
                          {action.description}
                        </Text>

                        <Button
                          variant={action.primary ? 'primary' : 'secondary'}
                          onClick={action.action}
                          disabled={action.disabled}
                          fullWidth
                        >
                          Go to {action.title}
                        </Button>
                      </BlockStack>
                    </Box>
                  </Card>
                </Grid.Cell>
              ))}
            </Grid>
          </BlockStack>
        </Layout.Section>

        {/* System Health */}
        <Layout.Section>
          <Card>
            <Box padding="100">
              <BlockStack gap="400">
                <Text variant="headingSm" as="h3">
                  System Health
                </Text>

                <InlineStack align="space-between" blockAlign="center">
                  <InlineStack gap="200" blockAlign="center">
                    <Icon
                      source={systemStatus.status === 'success' ? CheckCircleIcon : AlertTriangleIcon}
                      tone={systemStatus.status === 'success' ? 'success' : systemStatus.status === 'warning' ? 'warning' : 'critical'}
                    />
                    <Text variant="bodyMd">
                      {systemStatus.message}
                    </Text>
                  </InlineStack>

                  {systemHealth && (
                    <Badge
                      tone={systemHealth.healthScore === 100 ? 'success' : systemHealth.healthScore >= 80 ? 'attention' : 'critical'}
                    >
                      Health: {systemHealth.healthScore}%
                    </Badge>
                  )}
                </InlineStack>

                {systemHealth && (
                  <InlineStack gap="600" wrap={false}>
                    <Text variant="bodySm" color="subdued">
                      <strong>Active Brands:</strong> {systemHealth.activeBrands}/{systemHealth.totalBrands}
                    </Text>
                    <Text variant="bodySm" color="subdued">
                      <strong>Sync Errors:</strong> {systemHealth.syncErrors.length}
                    </Text>
                    <Text variant="bodySm" color="subdued">
                      <strong>Total Orders:</strong> {analytics?.totalOrders || 0}
                    </Text>
                  </InlineStack>
                )}
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}